import { useState } from "react";
import {Calculator, TrendingUp, Users} from 'lucide-react';

type TabType = "index" | "course" | "scramble";

function HandicapCalculator() {
  const [activeTab, setActiveTab] = useState<TabType>("index");

  // Handicap Index Calculator State
  const [grossScore, setGrossScore] = useState("");
  const [courseRating, setCourseRating] = useState("");
  const [slopeRating, setSlopeRating] = useState("");
  const [indexResult, setIndexResult] = useState<number | null>(null);

  // USGA Course Handicap Calculator State
  const [handicapIndex, setHandicapIndex] = useState("");
  const [courseSlope, setCourseSlope] = useState("");
  const [courseRatingCourse, setCourseRatingCourse] = useState("");
  const [coursePar, setCoursePar] = useState("");
  const [courseResult, setCourseResult] = useState<number | null>(null);

  // Scramble Calculator State
  const [scrambleType, setScrambleType] = useState<"2man" | "4man">("2man");
  const [playerAIndex, setPlayerAIndex] = useState("");
  const [playerBIndex, setPlayerBIndex] = useState("");
  const [playerCIndex, setPlayerCIndex] = useState("");
  const [playerDIndex, setPlayerDIndex] = useState("");
  const [scrambleResult, setScrambleResult] = useState<number | null>(null);

  // Tab A: Handicap Index Calculator
  const calculateHandicapIndex = () => {
    const score = parseFloat(grossScore);
    const rating = parseFloat(courseRating);
    const slope = parseFloat(slopeRating);

    if (isNaN(score) || isNaN(rating) || isNaN(slope) || slope === 0) {
      alert("Please enter valid numbers for all fields");
      return;
    }

    // Formula: (Score - Rating) * 113 / Slope
    const result = ((score - rating) * 113) / slope;
    setIndexResult(Math.round(result * 10) / 10);
  };

  // Tab B: USGA Course Handicap Calculator
  const calculateCourseHandicap = () => {
    const index = parseFloat(handicapIndex);
    const slope = parseFloat(courseSlope);
    const rating = parseFloat(courseRatingCourse);
    const par = parseFloat(coursePar);

    if (isNaN(index) || isNaN(slope) || isNaN(rating) || isNaN(par) || slope === 0) {
      alert("Please enter valid numbers for all fields");
      return;
    }

    // Formula: Index * (Slope / 113) + (Rating - Par)
    const result = index * (slope / 113) + (rating - par);
    setCourseResult(Math.round(result));
  };

  // Tab C: Scramble Calculator
  const calculateScramble = () => {
    const a = parseFloat(playerAIndex);
    const b = parseFloat(playerBIndex);

    if (isNaN(a) || isNaN(b)) {
      alert("Please enter valid handicap indexes for players");
      return;
    }

    if (scrambleType === "2man") {
      // 2-man scramble: 35% of A + 15% of B
      const result = a * 0.35 + b * 0.15;
      setScrambleResult(Math.round(result * 10) / 10);
    } else {
      // 4-man scramble
      const c = parseFloat(playerCIndex);
      const d = parseFloat(playerDIndex);

      if (isNaN(c) || isNaN(d)) {
        alert("Please enter valid handicap indexes for all 4 players");
        return;
      }

      // 4-man scramble: 20% of A + 15% of B + 10% of C + 5% of D
      const result = a * 0.2 + b * 0.15 + c * 0.1 + d * 0.05;
      setScrambleResult(Math.round(result * 10) / 10);
    }
  };

  const tabs = [
    { id: "index" as TabType, label: "Handicap Index", icon: Calculator },
    { id: "course" as TabType, label: "USGA Course Handicap", icon: TrendingUp },
    { id: "scramble" as TabType, label: "Scramble Calculator", icon: Users },
  ];

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Tab Navigation */}
      <div className="flex flex-col sm:flex-row gap-2 mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-lg font-semibold transition-all ${
                activeTab === tab.id
                  ? "bg-[#006747] text-white shadow-lg"
                  : "bg-white text-gray-700 hover:bg-gray-50 shadow"
              }`}
            >
              <Icon size={20} />
              <span className="text-sm sm:text-base">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Calculator Content */}
      <div className="bg-white rounded-xl shadow-xl p-6 sm:p-8">
        {/* Tab A: Handicap Index Calculator */}
        {activeTab === "index" ? (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Handicap Index Calculator
            </h2>
            <p className="text-gray-600 mb-6">
              Calculate your handicap index from a single round
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gross Score
                </label>
                <input
                  type="number"
                  value={grossScore}
                  onChange={(e) => setGrossScore(e.target.value)}
                  placeholder="e.g., 95"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006747] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Course Rating
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={courseRating}
                  onChange={(e) => setCourseRating(e.target.value)}
                  placeholder="e.g., 72.5"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006747] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slope Rating
                </label>
                <input
                  type="number"
                  value={slopeRating}
                  onChange={(e) => setSlopeRating(e.target.value)}
                  placeholder="e.g., 113"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006747] focus:border-transparent"
                />
              </div>

              <button
                onClick={calculateHandicapIndex}
                className="w-full bg-[#006747] text-white py-3 rounded-lg font-semibold hover:bg-[#005239] transition-colors"
              >
                Calculate Handicap Index
              </button>

              {indexResult !== null ? (
                <div className="mt-6 p-6 bg-[#F0FDF4] border-2 border-[#22C55E] rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Your Handicap Index:</p>
                  <p className="text-4xl font-bold text-[#006747]">{indexResult}</p>
                  <a
                    href="/formulas#handicap-index"
                    className="text-sm text-[#006747] hover:underline mt-2 inline-block"
                  >
                    How was this calculated? →
                  </a>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Tab B: USGA Course Handicap Calculator */}
        {activeTab === "course" ? (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              USGA Course Handicap Calculator
            </h2>
            <p className="text-gray-600 mb-6">
              Convert your handicap index to a course handicap
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Handicap Index
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={handicapIndex}
                  onChange={(e) => setHandicapIndex(e.target.value)}
                  placeholder="e.g., 15.2"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006747] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Course Slope
                </label>
                <input
                  type="number"
                  value={courseSlope}
                  onChange={(e) => setCourseSlope(e.target.value)}
                  placeholder="e.g., 113"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006747] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Course Rating
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={courseRatingCourse}
                  onChange={(e) => setCourseRatingCourse(e.target.value)}
                  placeholder="e.g., 72.5"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006747] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Course Par
                </label>
                <input
                  type="number"
                  value={coursePar}
                  onChange={(e) => setCoursePar(e.target.value)}
                  placeholder="e.g., 72"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006747] focus:border-transparent"
                />
              </div>

              <button
                onClick={calculateCourseHandicap}
                className="w-full bg-[#006747] text-white py-3 rounded-lg font-semibold hover:bg-[#005239] transition-colors"
              >
                Calculate Course Handicap
              </button>

              {courseResult !== null ? (
                <div className="mt-6 p-6 bg-[#F0FDF4] border-2 border-[#22C55E] rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Your Course Handicap:</p>
                  <p className="text-4xl font-bold text-[#006747]">{courseResult}</p>
                  <a
                    href="/formulas#course-handicap"
                    className="text-sm text-[#006747] hover:underline mt-2 inline-block"
                  >
                    How was this calculated? →
                  </a>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Tab C: Scramble Calculator */}
        {activeTab === "scramble" ? (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Scramble Calculator
            </h2>
            <p className="text-gray-600 mb-6">
              Calculate team handicap for scramble tournaments
            </p>

            <div className="space-y-4">
              {/* Scramble Type Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scramble Type
                </label>
                <div className="flex gap-4">
                  <button
                    onClick={() => setScrambleType("2man")}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                      scrambleType === "2man"
                        ? "bg-[#006747] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    2-Man Scramble
                  </button>
                  <button
                    onClick={() => setScrambleType("4man")}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                      scrambleType === "4man"
                        ? "bg-[#006747] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    4-Man Scramble
                  </button>
                </div>
              </div>

              {/* Player A */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Player A Handicap Index (Lowest)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={playerAIndex}
                  onChange={(e) => setPlayerAIndex(e.target.value)}
                  placeholder="e.g., 10.5"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006747] focus:border-transparent"
                />
              </div>

              {/* Player B */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Player B Handicap Index
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={playerBIndex}
                  onChange={(e) => setPlayerBIndex(e.target.value)}
                  placeholder="e.g., 15.2"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006747] focus:border-transparent"
                />
              </div>

              {/* Players C & D for 4-man */}
              {scrambleType === "4man" ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Player C Handicap Index
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={playerCIndex}
                      onChange={(e) => setPlayerCIndex(e.target.value)}
                      placeholder="e.g., 20.0"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006747] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Player D Handicap Index (Highest)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={playerDIndex}
                      onChange={(e) => setPlayerDIndex(e.target.value)}
                      placeholder="e.g., 25.0"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006747] focus:border-transparent"
                    />
                  </div>
                </>
              ) : null}

              <button
                onClick={calculateScramble}
                className="w-full bg-[#006747] text-white py-3 rounded-lg font-semibold hover:bg-[#005239] transition-colors"
              >
                Calculate Team Handicap
              </button>

              {scrambleResult !== null ? (
                <div className="mt-6 p-6 bg-[#F0FDF4] border-2 border-[#22C55E] rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">
                    {scrambleType === "2man" ? "2-Man" : "4-Man"} Scramble Team Handicap:
                  </p>
                  <p className="text-4xl font-bold text-[#006747]">{scrambleResult}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {scrambleType === "2man"
                      ? "Formula: 35% of A + 15% of B"
                      : "Formula: 20% of A + 15% of B + 10% of C + 5% of D"}
                  </p>
                  <a
                    href="/scramble-tools#how-it-works"
                    className="text-sm text-[#006747] hover:underline mt-2 inline-block"
                  >
                    How was this calculated? →
                  </a>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default HandicapCalculator;
